import { Popover } from '@kobalte/core/popover'
import { createSignal, Show, type JSX } from 'solid-js'
import type { CompanySelectorScope } from 'mormaii-web-presentation/company-selector'
import type { ThemeOptionScope } from 'mormaii-web-presentation/restricted'
import { CompanyIdentityPicker } from '../company-picker'
import { ThemeChoice } from '../theme-choice'
import { Icon } from '../icons'
import Css from './shell.module.scss'

type HeaderProps = {
    title: string
    subtitle?: string
    icon?: string
    userName?: string
    roleLabel?: string
    identityLoading: boolean

    /** So existe para quem administra mais de uma empresa. */
    companySelector?: CompanySelectorScope

    /** O menu do nome: a aparencia, "Meus dados" e "Sair". */
    themes: ThemeOptionScope[]
    onOpenMyData: () => void
    onSignOut: () => void

    onOpenMenu: () => void
}

export function Header(props: HeaderProps): JSX.Element {
    return (
        <header class={Css.header}>
            <button type="button" aria-label="Abrir menu" onClick={props.onOpenMenu} class={Css.openMenu}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 7h16M4 12h16M4 17h16" stroke-linecap="round" />
                </svg>
            </button>

            <div class={Css.section}>
                <Show when={props.icon}>
                    <span class={Css.sectionIcon}>
                        <Icon name={props.icon} size={19} />
                    </span>
                </Show>

                <div class={Css.sectionTexts}>
                    <h1 class={Css.sectionTitle}>{props.title}</h1>
                    <Show when={props.subtitle}>{(text) => <p class={Css.sectionSubtitle}>{text()}</p>}</Show>
                </div>
            </div>

            {/*
                A escolha de empresa fica AQUI, e nao dentro de cada tela: para quem
                administra mais de uma ela nao e filtro de lista nenhuma - e o contexto
                em que todas as telas trabalham, e sobrevive a troca de tela.

                E fica DENTRO da identidade, e nao ao lado dela: o nome da empresa ja
                estava abaixo do nome de quem esta logado, dizendo em qual empresa se
                esta. Um combo proprio repetiria essa resposta em barra de ferramenta,
                anunciando uma escolha que se faz poucas vezes por dia.
            */}
            <UserChip
                name={props.userName}
                role={props.roleLabel}
                loading={props.identityLoading}
                companySelector={props.companySelector}
                themes={props.themes}
                onOpenMyData={props.onOpenMyData}
                onSignOut={props.onSignOut}
            />
        </header>
    )
}

/**
 * Quem esta logado - e o MENU DO NOME (desde 08/10/2026).
 *
 * O avatar e o nome abrem um menu com a aparencia do painel, "Meus dados" e "Sair": e
 * onde se procura o que e da pessoa, a um clique de qualquer tela. A linha de baixo
 * continua sendo a escolha de empresa, com o seletor dela - ele tem busca para o
 * administrador geral, e nao caberia dentro deste menu.
 *
 * Enquanto o nome nao chega mostra um esqueleto pulsando, e nao o e-mail: o nome vem de
 * uma requisicao a mais depois da sessao, e trocar e-mail por nome na cara do leitor e
 * pior do que esperar por ele.
 */
function UserChip(props: {
    name?: string
    role?: string
    loading: boolean

    /** So existe para quem administra mais de uma empresa - e entao a linha de baixo escolhe. */
    companySelector?: CompanySelectorScope

    themes: ThemeOptionScope[]
    onOpenMyData: () => void
    onSignOut: () => void
}): JSX.Element {
    const [open, setOpen] = createSignal(false)
    let anchor: HTMLDivElement | undefined

    const initials = () =>
        (props.name ?? '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('')

    const toggle = () => setOpen(!open())
    const close = (then: () => void) => {
        setOpen(false)
        then()
    }

    return (
        <Show
            when={!props.loading}
            fallback={
                <div class={Css.identity}>
                    <div class={Css.skeleton}>
                        <div class={Css.skeletonAvatar} />
                        <div class={Css.skeletonTexts}>
                            <div class={Css.skeletonName} />
                            <div class={Css.skeletonRole} />
                        </div>
                    </div>
                </div>
            }
        >
            <Popover open={open()} onOpenChange={setOpen} placement="bottom-end" gutter={8}>
                <Popover.Anchor ref={anchor} class={Css.identity}>
                    <button
                        type="button"
                        class={Css.initials}
                        aria-haspopup="dialog"
                        aria-expanded={open()}
                        aria-label={`Menu de ${props.name ?? 'quem está logado'}: aparência, meus dados e sair`}
                        onClick={toggle}
                    >
                        {initials()}
                    </button>
                    <div class={Css.identityTexts}>
                        <button type="button" class={Css.nameButton} aria-expanded={open()} onClick={toggle}>
                            <span class={Css.name}>{props.name}</span>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2.5"
                                class={Css.nameArrow}
                            >
                                <path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <Show when={props.companySelector} fallback={<span class={Css.role}>{props.role}</span>}>
                            {(selector) => <CompanyIdentityPicker scope={selector()} label={props.role} />}
                        </Show>
                    </div>
                </Popover.Anchor>

                <Popover.Portal>
                    <Popover.Content
                        class={Css.userMenu}
                        aria-label="Menu do usuário"
                        // O avatar e o nome sao os gatilhos: clicar neles com o menu aberto
                        // nao e "clique fora" - e o proprio botao fechando o menu.
                        onInteractOutside={(event) => {
                            if (anchor?.contains(event.target as Node)) {
                                event.preventDefault()
                            }
                        }}
                    >
                        <p class={Css.menuTitle}>Aparência</p>
                        <ThemeChoice options={props.themes} />

                        <div class={Css.menuSeparator} />

                        <button type="button" class={Css.menuItem} onClick={() => close(props.onOpenMyData)}>
                            Meus dados
                        </button>
                        <button type="button" class={Css.menuItem} onClick={() => close(props.onSignOut)}>
                            Sair
                        </button>
                    </Popover.Content>
                </Popover.Portal>
            </Popover>
        </Show>
    )
}
